import { useState } from "react"
import { css } from "styled-components"
import { usePushWalletContext } from "@pushchain/ui-kit"

import { Box, Copy, SeasonThreePoints, Text } from "../../../blocks"
import { useGetSeasonThreeUserByWallet } from "../../../queries"
import { walletToFullCAIP10 } from "../../../helpers/web3helper"
import { Image } from "../../../css/SharedStyling"
import ReferralImage from "../../../../static/assets/website/rewards/ReferralCard.webp"
import { device } from "../../../config/globals"

type ReferralBenefitProps = {
  text: string;
}

const ReferralBenefit = ({ text }: ReferralBenefitProps) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap="spacing-xs"
      width="100%"
    >
      <SeasonThreePoints width={21} height={21} />
      <Text
        variant="bs-regular"
        css={css`
          color: rgba(255, 255, 255, 0.75);
          flex: 1;
        `}
      >
        {text}
      </Text>
    </Box>
  );
};


export const ReferralProgram = () => {
  const [copied, setCopied] = useState(false);

  const { universalAccount } = usePushWalletContext('wallet1');
  const caip10WalletAddress = walletToFullCAIP10(
    universalAccount?.address as string,
    universalAccount?.chain,
  );
  const { data: userDetails } = useGetSeasonThreeUserByWallet({ walletAddress: caip10WalletAddress });

  const referralUrl = userDetails?.userId
    ? `portal.push.org/rewards?ref=${userDetails.userId}`
    : null;

  const handleCopy = () => {
    if (!referralUrl) return;
    const fullUrl = `https://${referralUrl}`;

    const confirm = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const fallback = () => {
      const el = document.createElement('textarea');
      el.value = fullUrl;
      el.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try { document.execCommand('copy'); confirm(); } finally { document.body.removeChild(el); }
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(fullUrl).then(confirm).catch(fallback);
    } else {
      fallback();
    }
  };

  const benefits = [
    {
      text: "You earn 16% Points generated by your invites"
    },
    {
      text: "You earn 8% Points generated by the people they invite"
    },
    {
      text: "Unlock invites by ensuring your invites progress to Lv. 7"
    }
  ];

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="space-between"
      padding="spacing-md"
      borderRadius="radius-md"
      position="relative"
      css={css`
        flex: 65;
        min-width: 0;
        background: radial-gradient(109.87% 94.08% at 50% 5.92%, #131220 75.21%, #291D39 100%);
        box-sizing: border-box;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1px;
          background: rgba(255, 255, 255, 0.10);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
      `}
    >
      <Box
        display="flex"
        flexDirection="column"
        gap="spacing-xs"
        css={css`
            flex: 1;
            min-width: 0;
            height: 100%;
            `}
      >
        <Text
          variant="h3-semibold"
          css={css`
            text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
            background: linear-gradient(180deg, rgba(255, 255, 255, 1) 50%, rgba(201, 104, 231, 1) 100%);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
          `}
        >
          S3 Referral Program
        </Text>

        <Box display="flex" flexDirection="column" gap="spacing-sm">
          {benefits.map((benefit, index) => (
            <ReferralBenefit key={index} text={benefit.text} />
          ))}
        </Box>

        {referralUrl && (
          <Box
            display="flex"
            alignItems="center"
            gap="spacing-xxs"
            onClick={handleCopy}
            css={css`
              cursor: pointer;
              padding: 12px;
              border-radius: 16px;
              border: 1px solid rgba(255, 255, 255, 0.25);
              transition: background 0.15s ease;
              max-width: fit-content;
              margin-top: auto;
              &:hover {
                background: rgba(255, 255, 255, 0.05);
              }
            `}
          >
            <Text
              variant="bs-regular"
              color="#ffff"
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                flex: 1;
                min-width: 0;
              `}
            >
              {referralUrl}
            </Text>
            <Text variant="c-regular" color={copied ? '#68FFB4' : 'text-tertiary'} css={css`flex-shrink: 0; font-size: 11px;`}>
              {copied ? 'Copied!' : <Copy color='white' size={15} />}
            </Text>
          </Box>
        )}
      </Box>

      <Box
        position="relative"
        css={css`
          width: 200px;
          flex-shrink: 0;
          align-self: stretch;
          overflow: hidden;

          @media ${device.tablet} {
            display: none;
          }
        `}
      >
        <Image
          src={ReferralImage}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'right center',
          }}
        />
      </Box>
    </Box>
  );
};
